import * as React from "react";
import {
  ArrowUpRight,
  CalendarCheck,
  Gift,
  LifeBuoy,
  MessageSquare,
  Rocket,
  Wrench,
} from "lucide-react";
import { HandNote, SketchArrow } from "@/components/Sketch";
import { useInView } from "@/hooks/useInView";

const STEPS = [
  {
    id: 1,
    icon: MessageSquare,
    title: "Anfrage",
    text: "Du erzählst uns kurz von deinem Unternehmen und deinen Vorstellungen.",
    note: "In 3 Sekunden erledigt",
  },
  {
    id: 2,
    icon: CalendarCheck,
    title: "Kostenloses Erstgespräch",
    text: "Wir besprechen Ziele, Inhalte, Stil und Anforderungen – unverbindlich.",
    note: "Mit unserem netten Team",
  },
  {
    id: 3,
    icon: Gift,
    title: "Demo",
    badge: "kostenlos",
    text: "Du siehst einen ersten individuellen Entwurf, bevor du dich entscheidest.",
    note: "Wir glauben kaum, dass du nicht überzeugt bist",
    featured: true,
  },
  {
    id: 4,
    icon: Wrench,
    title: "Umsetzung",
    text: "Nach deiner Freigabe erstellen wir die vollständige Webseite.",
    note: "Mit Expertise an die Arbeit",
  },
  {
    id: 5,
    icon: Rocket,
    title: "Livegang",
    text: "Domain, Formulare, mobile Ansicht und Technik werden verbunden und geprüft.",
    note: "Innerhalb von 1–3 Wochen",
  },
  {
    id: 6,
    icon: LifeBuoy,
    title: "Betreuung",
    text: "Wir kümmern uns weiter um Hosting, Wartung und vereinbarte Anpassungen.",
    note: "Auf eine lange, gute Zusammenarbeit",
  },
];

const StepCard = ({ step, inView, align }) => {
  const { featured } = step;
  return (
    <article
      className={`relative flex flex-col border p-6 transition-[opacity,transform,box-shadow,border-color] duration-[900ms] ease-out sm:p-7 ${
        featured
          ? "border-[hsl(222_80%_58%/0.55)] bg-[hsl(0_0%_100%/0.97)] shadow-[0_36px_74px_-46px_hsl(225_60%_30%/0.7)]"
          : "border-light-border bg-[hsl(0_0%_100%/0.8)] shadow-[0_24px_54px_-44px_hsl(225_40%_25%/0.55)]"
      } ${align === "right" ? "lg:text-right" : ""} ${
        inView
          ? "translate-x-0 translate-y-0 opacity-100"
          : `${align === "right" ? "lg:-translate-x-8" : "lg:translate-x-8"} translate-y-6 opacity-0`
      } hover:-translate-y-1.5 hover:shadow-[0_40px_80px_-46px_hsl(225_50%_28%/0.65)]`}
    >
      <div
        className={`flex flex-wrap items-center gap-3 ${
          align === "right" ? "lg:justify-end" : ""
        }`}
      >
        <h3 className="font-display text-lg font-light tracking-wide text-light-foreground sm:text-xl">
          {step.title}
        </h3>
        {step.badge && (
          <span
            className={`px-2.5 py-1 font-display text-[9px] font-medium uppercase tracking-[0.2em] ${
              featured
                ? "bg-gradient-cta text-primary-foreground"
                : "border border-[hsl(222_70%_60%/0.45)] text-[hsl(222_85%_45%)]"
            }`}
          >
            {step.badge}
          </span>
        )}
      </div>

      <p className="mt-3 text-sm font-light leading-relaxed text-light-muted">
        {step.text}
      </p>

      {/* Notiz auf Mobile direkt in der Karte */}
      <HandNote
        absolute={false}
        tone="dark"
        fontSize="20px"
        className={`mt-5 lg:hidden ${align === "right" ? "lg:text-right" : ""}`}
      >
        {step.note}
      </HandNote>
    </article>
  );
};

/** Notiz mit Pfeil, die auf die Karte zeigt (ab lg). */
const StepNote = ({ step, inView, side }) => (
  <div
    className={`relative hidden h-full min-h-[150px] items-center lg:flex ${
      side === "left" ? "justify-start pl-6" : "justify-end pr-6"
    } transition-[opacity,transform] duration-[900ms] ease-out ${
      inView ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
    }`}
    style={{ transitionDelay: "260ms" }}
  >
    <div className="relative max-w-[260px]">
      <HandNote
        absolute={false}
        tone="dark"
        fontSize="26px"
        align={side === "left" ? "left" : "right"}
        className={side === "left" ? "pl-10" : "pr-10"}
      >
        {step.note}
      </HandNote>

      <SketchArrow
        tone="dark"
        flipX={side === "left"}
        className={`h-12 w-12 ${
          side === "left" ? "-left-2 top-1" : "-right-2 top-1"
        }`}
      />
    </div>
  </div>
);

const TimelineStep = ({ step, index }) => {
  const [ref, inView] = useInView({ threshold: 0.3 });
  const cardLeft = index % 2 === 0; // Karte links (ab lg)
  const { icon: Icon, featured } = step;

  return (
    <li
      ref={ref}
      className="grid grid-cols-[56px_minmax(0,1fr)] items-center gap-5 lg:grid-cols-[minmax(0,1fr)_112px_minmax(0,1fr)] lg:gap-0"
    >
      {/* Knoten */}
      <div className="relative col-start-1 row-start-1 flex justify-center lg:col-start-2">
        <span
          className={`relative z-10 flex h-14 w-14 items-center justify-center rounded-full border transition-[transform,opacity] duration-700 ease-out ${
            featured
              ? "border-transparent bg-gradient-cta text-primary-foreground shadow-[0_18px_40px_-16px_hsl(230_90%_50%/0.7)]"
              : "border-light-border bg-[hsl(0_0%_100%)] text-[hsl(222_85%_45%)] shadow-[0_14px_30px_-20px_hsl(225_40%_25%/0.7)]"
          } ${inView ? "scale-100 opacity-100" : "scale-75 opacity-0"}`}
        >
          <Icon className="h-5 w-5" strokeWidth={1.5} />
          {featured && (
            <span
              aria-hidden
              className="absolute inset-0 animate-ping rounded-full border border-[hsl(222_90%_55%/0.5)]"
            />
          )}
          <span
            aria-hidden
            className={`absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full border text-[9px] font-medium ${
              featured
                ? "border-[hsl(222_80%_58%)] bg-[hsl(0_0%_100%)] text-[hsl(222_85%_42%)]"
                : "border-light-border bg-[hsl(214_40%_96%)] text-light-muted"
            }`}
          >
            {step.id}
          </span>
        </span>
      </div>

      {/* Karte */}
      <div
        className={`col-start-2 row-start-1 ${
          cardLeft ? "lg:col-start-1 lg:pr-12" : "lg:col-start-3 lg:pl-12"
        }`}
      >
        <StepCard step={step} inView={inView} align={cardLeft ? "right" : "left"} />
      </div>

      {/* Notiz gegenüber der Karte */}
      <div
        className={`row-start-1 hidden lg:block ${
          cardLeft ? "lg:col-start-3" : "lg:col-start-1"
        }`}
      >
        <StepNote step={step} inView={inView} side={cardLeft ? "left" : "right"} />
      </div>
    </li>
  );
};

export const ProcessSteps = () => {
  const [ref, inView] = useInView({ threshold: 0.05 });
  const trackRef = React.useRef(null);
  const [progress, setProgress] = React.useState(0);

  React.useEffect(() => {
    const node = trackRef.current;
    if (!node) return undefined;

    let frame = 0;
    const update = () => {
      frame = 0;
      const rect = node.getBoundingClientRect();
      const anchor = window.innerHeight * 0.55;
      setProgress(Math.min(Math.max((anchor - rect.top) / rect.height, 0), 1));
    };
    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <section
      id="ablauf"
      ref={ref}
      className="relative isolate overflow-hidden bg-light text-light-foreground"
      style={{
        clipPath: "polygon(0 2.6vw, 100% 0, 100% 100%, 0 calc(100% - 2vw))",
      }}
    >
      <div aria-hidden className="absolute inset-0 -z-10 bg-gradient-light" />
      <div
        aria-hidden
        className="absolute -right-24 top-[4%] -z-10 h-[420px] w-[540px] rotate-6 rounded-[45%] bg-[radial-gradient(closest-side,hsl(214_80%_72%/0.26),transparent)] blur-2xl"
      />
      <div
        aria-hidden
        className="absolute -left-20 bottom-[4%] -z-10 h-[400px] w-[500px] rounded-full bg-[radial-gradient(closest-side,hsl(265_70%_74%/0.18),transparent)] blur-2xl"
      />

      <div className="mx-auto max-w-[1500px] px-5 pb-28 pt-32 sm:px-8 sm:pb-32 sm:pt-36 lg:px-12">
        <div
          className={`flex flex-col gap-8 transition-[opacity,transform] duration-700 ease-out lg:flex-row lg:items-end lg:justify-between ${
            inView ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
          }`}
        >
          <div className="max-w-2xl">
            <p className="font-display text-[11px] font-light uppercase tracking-[0.32em] text-light-muted">
              So läuft die Zusammenarbeit
            </p>
            <h2 className="mt-6 font-display text-3xl font-light leading-tight tracking-tight text-light-foreground sm:text-4xl lg:text-5xl">
              In sechs Schritten
              <br className="hidden sm:block" /> zu deiner Webseite.
            </h2>
            <div
              aria-hidden
              className="mt-7 h-px w-24 bg-[linear-gradient(90deg,hsl(222_90%_45%),hsl(265_70%_58%/0.15))]"
            />
          </div>

          <p className="max-w-md text-sm font-light leading-relaxed text-light-muted sm:text-base">
            Klar, ruhig und ohne Risiko: Erstgespräch und erste Demo sind
            kostenlos – du entscheidest erst, wenn du dein Design gesehen hast.
          </p>
        </div>

        <div ref={trackRef} className="relative mt-16 lg:mt-20">
          <div
            aria-hidden
            className="absolute inset-y-0 left-[27px] w-px bg-light-border lg:left-1/2 lg:-translate-x-1/2"
          >
            <span
              className="absolute inset-x-0 top-0 block bg-[linear-gradient(180deg,hsl(222_90%_50%),hsl(265_75%_60%))]"
              style={{ height: `${progress * 100}%` }}
            />
          </div>

          <ol className="relative flex flex-col gap-12 lg:gap-10">
            {STEPS.map((step, index) => (
              <TimelineStep key={step.id} step={step} index={index} />
            ))}
          </ol>
        </div>

        <div
          className={`mt-16 flex flex-col items-start gap-6 border-t border-light-border pt-10 transition-[opacity,transform] duration-700 sm:flex-row sm:items-center sm:justify-between ${
            inView ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"
          }`}
        >
          <p className="text-sm font-light leading-relaxed text-light-muted">
            Erster Entwurf in 14 Tagen · monatlich kündbar · persönlicher
            Ansprechpartner
          </p>

          <a href="#kontakt" className="group flex items-center gap-2 outline-none">
            <span
              className="flex items-center gap-2 bg-gradient-cta px-7 py-3.5 font-display text-sm font-normal tracking-wide text-primary-foreground transition-transform duration-300 group-hover:-translate-y-0.5"
              style={{ transform: "skewX(-12deg)" }}
            >
              <span
                className="flex items-center gap-2"
                style={{ transform: "skewX(12deg)" }}
              >
                Kostenlose Demo anfragen
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
            </span>
          </a>
        </div>
      </div>
    </section>
  );
};
